import { Injectable } from '@angular/core';
import { logFeature } from '@office-start/telemetry-service';
import { KnownFeatureNames } from '@office-start/telemetry-types';
import { KnownFeatureInstrumentationEvents } from '@office-start/telemetry-types/dist/KnownInstrumentationEvents';

import { OfficeHomePageErrorHandler } from '../../error-handler/error-handler.service';
import { ErrorId } from '../../error-handler/error-id';
import { DeviceDetector } from '../services/device-detector.service';
import { LocalStorageService } from '../services/local-storage.service';
import { UnloadEventService } from '../services/unload-event.service';
import { UserDataStorageService } from '../services/user-data-storage.service';
import { ClientStartupConfigService } from '../shared-context/client-startup-config.service';
import { DispatcherEventFactoryService } from './dispatcher-instrumentation.service';
import { InstrumentationEvent, InstrumentationParam, InstrumentationParams, InstrumentationService, KnownAreas, KnownInstrumentationEvents } from './instrumentation.service';

/**
 * User profile metric record.
 * Keep in sync with UserProfileMetric.cs file on server.
 */
export interface EventMetric {
    /**
     * Number of event occurrences.
     */
    count: number;

    /**
     * Timestamp of the first occurrence.
     */
    firstOccurrence: number;

    /**
     * Timestamp of the last occurrence.
     */
    lastOccurrence: number;
}

/**
 * Type alias for collection of named event metrics.
 */
export type EventMetrics = { [param: string]: EventMetric; };

/**
 * Interface defining user profile.
 */
export interface UserProfile {
    /**
     * Usage metrics stored with user profile.
     */
    metrics: EventMetrics;
}

// Keep in sync with UserProfileMetricsV1.cs file on server.
class EventMetricsSnapshot {
    public metrics: EventMetrics;
    public created: number;
    public saved: number;
}

/**
 * Implementation of user profile service interface.
 */
@Injectable({
    providedIn: 'root'
})
export class UserProfileService {
    private static readonly BufferingTimespanInSecondsForDesktop = 30;
    private static readonly BufferingTimespanInSecondsForMobile = 5;
    private static readonly LoadFromUserStorageRemoteAttempts = 2;
    private static readonly LocalStorageSchemaVersion = 1;
    private static readonly MillisecondsInDay = 86400000; // 1000 (ms in sec) * 60 (sec in min) * 60 (min in hr) * 24 (hr in day)

    // This metric name is used to make quick cut off decision on whether to run sanitation logic which trims
    // off metrics that are no longer needed. Site_Visit metric was incremented on each app load and thus
    // guaranteed to be present in old set. If that metric is not present then metric set is considered as
    // already sanitized.
    private static readonly MetricPresentInSetThatHasToBeSanitized = 'Site_Visit';

    private readonly bufferingTimeoutInSeconds: number;
    private scheduledSavePromise: Promise<any> = null;
    private scheduledSaveTimeoutId: ReturnType<typeof setTimeout>;
    private scheduledSaveEstimatedTime: number;
    private loadingMetricsFromRoamingStorage: boolean;
    private pendingLoad: Promise<UserProfile>;
    private forceImmediateSave: boolean;

    private readonly inMemoryProfile: UserProfile;
    private fullMetrics: EventMetricsSnapshot;
    private unsavedMetrics: EventMetricsSnapshot;
    private readonly firstTimeUser: boolean;
    private readonly firstVisitTimestamp: number;

    private readonly roamingStorageProfileDataKey: string;
    private readonly localStorageProfileDataKey: string;
    private readonly localStorageUnsavedDataKey: string;

    private pendingFirstTimeMetrics: { [param: string]: number; } = {};

    private readonly onLoadMetrics: EventMetrics;

    constructor(
        private readonly clientStartupConfigService: ClientStartupConfigService,
        private readonly localStorageService: LocalStorageService,
        unloadEventService: UnloadEventService,
        deviceDetector: DeviceDetector,
        private readonly userDataStorageService: UserDataStorageService,
        private readonly instrumentationService: InstrumentationService,
        private readonly globalErrorHandler: OfficeHomePageErrorHandler
    ) {
        // Determine if this is first time user based on the fact that there is no user profile stored in roaming and local storages
        this.firstTimeUser = this.userDataStorageService.isFirstUse();
        this.firstVisitTimestamp = this.userDataStorageService.getFirstUseTimestamp();

        // Use shorter buffering timespan on mobile devices to account for the fact that we are seeing much lower
        // success rate for handling beforeunload/pagehide (especially on iOS). We have a few short-lived page
        // sessions (under 30 sec) so this optimization will reduce the occasions when user profile does not
        // get stored in roaming storage.
        this.bufferingTimeoutInSeconds = deviceDetector.isMobileDevice()
            ? UserProfileService.BufferingTimespanInSecondsForMobile
            : UserProfileService.BufferingTimespanInSecondsForDesktop;

        // Load cached user metrics from local storage
        this.localStorageProfileDataKey = `.Full_Metrics.v${UserProfileService.LocalStorageSchemaVersion}`;
        this.fullMetrics = this.localStorageService.get(this.localStorageProfileDataKey);
        // If this is first time visit or local storage was cleared/corrupt then initialize to empty set of metrics
        if (!this.fullMetrics || !this.fullMetrics.metrics) {
            this.fullMetrics = UserProfileService.createEmptySnapshot();
        } else {
            // Update "first visit" properties to account for the case when we have been recording
            // user profile in local storage only
            if (this.firstTimeUser) {
                this.firstTimeUser = false;
                this.firstVisitTimestamp = this.fullMetrics.created;
            }
        }

        this.onLoadMetrics = this.fullMetrics.metrics;

        // Load set of metrics that were not saved to roaming user storage
        this.localStorageUnsavedDataKey = `.Unsaved_Metrics.v${UserProfileService.LocalStorageSchemaVersion}`;
        this.unsavedMetrics = this.localStorageService.get(this.localStorageUnsavedDataKey);
        // Ensure data in local storage was not corrupted
        if (!this.unsavedMetrics || !this.unsavedMetrics.metrics) {
            this.unsavedMetrics = null;
        } else {
            if (this.firstTimeUser) {
                this.firstTimeUser = false;
                this.firstVisitTimestamp = this.unsavedMetrics.created;
            }
        }

        this.inMemoryProfile = {
            metrics: this.fullMetrics.metrics
        };

        // Immediately attempt loading user metrics from roaming storage
        this.roamingStorageProfileDataKey = `User_Profile.Metrics.v${UserProfileService.LocalStorageSchemaVersion}`;
        this.loadFromUserStorage();

        // If this is first ever use or there are unsaved metrics then force save operation at first opportunity
        this.forceImmediateSave = this.firstTimeUser || !!this.unsavedMetrics;

        // Handle window unload and force saving of buffered metrics
        unloadEventService.addUnloadEvent(() => this.flush());
    }

    /**
     * Increments the occurence of metric with given name.
     * If provided, maxDelayInSeconds will ensure the metric is written to storage within the given time interval.
     * Passing 0 results in the default delay rather than an immediate save.
     */
    public incrementMetric(name: string, maxDelayInSeconds?: number): void {
        const timestamp = UserProfileService.createTimestamp();

        // Increment must be always applied to fullMetrics, even when loading from roaming storage is underway.
        // This will account for the case when there is an error accessing storage.
        UserProfileService.applyIncrement(name, timestamp, this.fullMetrics.metrics);

        // Check if metric was encountered for first time
        const metric = this.fullMetrics.metrics[name];
        if (metric && metric.count === 1) {
            // If metrics have not been loaded from roaming storage yet then record this as
            // "candidate" that needs to be validated once loading operation is over
            if (this.loadingMetricsFromRoamingStorage) {
                this.pendingFirstTimeMetrics[name] = metric.firstOccurrence;
            } else {
                this.logMetricRecordedForFirstTime(name);
            }
        }

        // Increment must be always applied to unsavedMetrics to prevent the loss in case there is failure to update
        // metrics stored in user storage.
        if (!this.unsavedMetrics) {
            this.unsavedMetrics = UserProfileService.createEmptySnapshot();
        }
        UserProfileService.applyIncrement(name, timestamp, this.unsavedMetrics.metrics);

        this.scheduleSave(maxDelayInSeconds);
    }

    /**
    * Flushes any buffered metrics.
    */
    public flush() {
        if (this.scheduledSavePromise) {
            this.forceSave();
        }
    }

    /**
     * Gets user profile.
     */
    public get(): Promise<UserProfile> {
        return this.pendingLoad;
    }

    /**
     * Gets a value indicating whether this is user's first visit to office.com.
     */
    public isFirstVisit(): boolean {
        return this.firstTimeUser;
    }

    public getInitialMetric(metric: string) {
        return this.onLoadMetrics[metric];
    }

    /**
     * Returns a promise with a boolean that returns true of the metric exists
     * @param verifyMetric: The metric you're searching for
     * @param exactMatch: If you want verifyMetric to be an exactMatch with a metric rather than the default partial match
     */
    public async doesMetricExist(verifyMetric: string, exactMatch?: boolean): Promise<boolean> {
        const profile = await this.get();
        const metrics = profile.metrics;
        for (const metricName in metrics) {
            const matches = exactMatch ? metricName === verifyMetric : metricName.includes(verifyMetric);
            if (matches) {
                return true;
            }
        }
        return false;
    }

    /**
     * Gets number of full days that passed since user visited office.com for the first time.
     * Negative value is returned to indicate "unknown" state (i.e. when we cannot tell).
     */
    public fullDaysSinceFirstVisit(): number {
        if (this.firstTimeUser) {
            return 0;
        }

        if (typeof this.firstVisitTimestamp === 'undefined' || this.firstVisitTimestamp < 0) {
            return -1;
        }

        const firstVisitAgeInMilliseconds = UserProfileService.createTimestamp() - this.firstVisitTimestamp;
        return Math.floor(firstVisitAgeInMilliseconds / UserProfileService.MillisecondsInDay);
    }

    private static createTimestamp(): number {
        return Date.now();
    }

    private static createEmptySnapshot(): EventMetricsSnapshot {
        return {
            metrics: {},
            created: UserProfileService.createTimestamp(),
            saved: -1
        };
    }

    private static applyIncrement(name: string, timestamp: number, metrics: EventMetrics) {
        if (metrics.hasOwnProperty(name)) {
            const metric = metrics[name];
            metric.count++;
            metric.lastOccurrence = timestamp;
        } else {
            metrics[name] = {
                count: 1,
                firstOccurrence: timestamp,
                lastOccurrence: timestamp
            };
        }
    }

    private static mergeMetrics(sink: EventMetrics, source: EventMetrics) {
        for (const metricName in source) {
            UserProfileService.applyMetric(sink, metricName, source[metricName]);
        }
    }

    private static applyMetric(sink: EventMetrics, name: string, metric: EventMetric) {
        if (sink.hasOwnProperty(name)) {
            const sinkMetric = sink[name];
            sinkMetric.count += metric.count;
            if (sinkMetric.firstOccurrence > metric.firstOccurrence) {
                sinkMetric.firstOccurrence = metric.firstOccurrence;
            }
            if (sinkMetric.lastOccurrence < metric.lastOccurrence) {
                sinkMetric.lastOccurrence = metric.lastOccurrence;
            }
        } else {
            sink[name] = { ...metric };
        }
    }

    private static sanitizeMetrics(metrics: EventMetrics): EventMetrics {
        // This will trim the set of metrics that are deemed excessive.
        // The quick cut off decision is based on precense of Site_Visit metric that is guaranteed to be present
        // among metrics that have to be removed. If that metric is not present then metric set is considered as
        // already sanitized.
        if (!metrics.hasOwnProperty(UserProfileService.MetricPresentInSetThatHasToBeSanitized)) {
            return metrics;
        }

        const sanitizedMetrics: EventMetrics = {};

        // Enumerate all metrics and keep only ones that we want
        for (const metricName in metrics) {
            // Metrics for Request events have to be removed
            if (metricName.indexOf('_Request') > 0) {
                continue;
            }

            // Metrics for Impression events have to be removed
            if (metricName.indexOf('_Impression') > 0) {
                continue;
            }

            let allowMetric = false;

            if (metricName.startsWith('Referral.')) {
                // Referral metrics are allowed
                allowMetric = true;
            } else if (metricName.indexOf('_PageView') > 0) {
                // PageView metrics are allowed
                allowMetric = true;
            } else if (metricName.indexOf('_Click') > 0 || metricName.indexOf('_Toggle') > 0) {
                // Click/toggle metrics in {EventName}.{Area}.{ElementId} format are allowed
                const metricNameComponents = metricName.split('.');
                if (metricNameComponents.length === 3) {
                    // We have cases of old metrics having three components so have to make sure that
                    // second component represents area
                    if (KnownAreas.hasOwnProperty(metricNameComponents[1])) {
                        allowMetric = true;
                    }
                }
            } else {
                // Specific properties that are allowed
                if (metricName === 'Upload_Completed') {
                    allowMetric = true;
                }
            }

            if (allowMetric) {
                sanitizedMetrics[metricName] = metrics[metricName];
            }
        }

        return sanitizedMetrics;
    }

    private resetScheduledSave(): void {
        if (this.scheduledSavePromise) {
            clearTimeout(this.scheduledSaveTimeoutId);
            this.scheduledSaveTimeoutId = null;
            this.scheduledSavePromise = null;
            this.scheduledSaveEstimatedTime = null;
        }
    }

    private scheduleSave(maxDelayInSeconds?: number): void {
        if (this.forceImmediateSave) {
            this.forceImmediateSave = false;
            this.forceSave();
            return;
        }

        // If there is already a save scheduled within the alloted time, do nothing.
        if (this.scheduledSavePromise && (!maxDelayInSeconds || ((Date.now() + maxDelayInSeconds * 1000) >= this.scheduledSaveEstimatedTime))) {
            return;
        }

        this.resetScheduledSave();

        // Allow for chaining changes by waiting for a period of time before saving
        const scheduledDelay = (maxDelayInSeconds ? Math.min(maxDelayInSeconds, this.bufferingTimeoutInSeconds) : this.bufferingTimeoutInSeconds) * 1000;
        this.scheduledSaveEstimatedTime = Date.now() + scheduledDelay;
        this.scheduledSavePromise = new Promise<void>(resolve => {
            this.scheduledSaveTimeoutId = setTimeout(() => {
                this.forceSave();
                resolve();
            }, scheduledDelay);
        });
    }

    private forceSave(): void {
        this.resetScheduledSave();

        // This will ensure the integrity of metrics that will be stored in local and roaming storages
        if (this.clientStartupConfigService.config.sanitizeMetrics) {
            if (this.unsavedMetrics) {
                this.unsavedMetrics.metrics = UserProfileService.sanitizeMetrics(this.unsavedMetrics.metrics);
            }
            this.fullMetrics.metrics = UserProfileService.sanitizeMetrics(this.fullMetrics.metrics);
        }

        // Cache full set of user metrics in local storage
        this.fullMetrics.saved = UserProfileService.createTimestamp();
        this.localStorageService.set(this.localStorageProfileDataKey, this.fullMetrics);

        // Store unsaved metrics in local storage in case subsequent user storage operation fails
        if (this.unsavedMetrics) {
            this.localStorageService.set(this.localStorageUnsavedDataKey, this.unsavedMetrics);
        }

        // Skip storing to roaming storage if load operation is still underway
        if (this.loadingMetricsFromRoamingStorage) {
            // Schedule save again so buffered changes eventually get stored
            this.scheduleSave();
        } else {
            // Preserve unsaved metrics in case save fails
            const pendingUnsavedMetrics = this.unsavedMetrics;
            this.unsavedMetrics = null;

            this.userDataStorageService.set<EventMetricsSnapshot>(this.roamingStorageProfileDataKey, this.fullMetrics)
                .then(() => {
                    // Update unsaved metrics in local storage since currently recorded unsaved metrics
                    // may include the metrics that just got saved
                    if (this.unsavedMetrics) {
                        this.localStorageService.set(this.localStorageUnsavedDataKey, this.unsavedMetrics);
                    } else {
                        this.localStorageService.remove(this.localStorageUnsavedDataKey);
                    }
                })
                .catch(error => {
                    // Pass caught error to global handler to ensure that it is properly logged in case it is code error
                    this.globalErrorHandler.handleError(error, ErrorId.SxWjc);

                    // Merge unsaved metrics that got collected while save operation was underway
                    if (this.unsavedMetrics && pendingUnsavedMetrics) {
                        UserProfileService.mergeMetrics(pendingUnsavedMetrics.metrics, this.unsavedMetrics.metrics);
                    }

                    this.unsavedMetrics = pendingUnsavedMetrics;

                    // Ensure that unsaved metrics are preserved in local storage
                    if (this.unsavedMetrics) {
                        this.localStorageService.set(this.localStorageUnsavedDataKey, this.unsavedMetrics);
                    }
                });
        }
    }

    private loadFromUserStorage(): void {
        this.pendingLoad = new Promise<UserProfile>(resolve => {
            this.loadingMetricsFromRoamingStorage = true;

            this.userDataStorageService.get<EventMetricsSnapshot>(this.roamingStorageProfileDataKey, UserProfileService.LoadFromUserStorageRemoteAttempts)
                .then(snapshot => {
                    // Returned value will be undefined in case user profile has not been saved in user storage yet
                    if (snapshot && snapshot.metrics) {
                        // Apply unsaved metrics if they have been created after metrics were saved
                        if (this.unsavedMetrics && snapshot.saved < this.unsavedMetrics.created) {
                            UserProfileService.mergeMetrics(snapshot.metrics, this.unsavedMetrics.metrics);
                        }

                        // Stop using cached metrics and expose the ones loaded from roaming storage
                        this.fullMetrics = snapshot;

                        // Update user profile to reference the loaded metrics
                        this.inMemoryProfile.metrics = this.fullMetrics.metrics;
                    }
                })
                .catch(error => {
                    this.globalErrorHandler.handleError(error, ErrorId.QN0a5);
                }) // TODO: replace with `finally()` when appropriate polyfill is added
                .then(() => {
                    this.loadingMetricsFromRoamingStorage = false;

                    // Check for first time metrics after loading from roaming storage is complete
                    for (const metricName in this.pendingFirstTimeMetrics) {
                        const metric = this.fullMetrics.metrics[metricName];
                        if (!metric || this.pendingFirstTimeMetrics[metricName] === metric.firstOccurrence) {
                            this.logMetricRecordedForFirstTime(metricName);
                        }
                    }

                    this.pendingFirstTimeMetrics = {};

                    // Make user profile available to external objects
                    resolve(this.inMemoryProfile);
                });
        });
    }

    private logMetricRecordedForFirstTime(metricName: string) {
        const params = {};
        params[InstrumentationParam.MetricName] = metricName;
        this.instrumentationService.logGenericEvent(KnownInstrumentationEvents.FirstTimeMetric, params);
        logFeature({featureName: KnownFeatureNames.Other, eventName: KnownFeatureInstrumentationEvents.FirstTimeMetric, params});
    }
}

/**
 * This class updates user profile by handling instrumentation event.
 */
class UserProfileInstrumentationEvent implements InstrumentationEvent {

    constructor(
        private readonly userProfileService: UserProfileService,
        public streamName: string,
        public params: InstrumentationParams,
        public metrics: Array<string>
    ) {
    }

    public record(): void {
        if (this.metrics) {
            // Click events which resulted in increment to referral metrics should be flushed immediately to ensure that
            // app tiles section accurately reflects workloads that user engaged with. Without explicit flush metrics will be
            // batched and will be recorded with delay which may impact what users sees immediately after referral click
            // in case new instance of the page is opened inside tab/browser or page is reloaded.
            // Some referral clicks result in other metrics updates, such as a start page app tile click which also updates
            // the PageView metric. We delay the flush by 1 second to attempt to batch all metric updates related to a single
            // click into one save operation, reducing the number of storage write requests.
            // We are adjusting the batch delay rather than calling flush() directly so as to prevent spamming of the API.
            const maxDelay = (this.streamName === KnownInstrumentationEvents.GenericClick && this.params[InstrumentationParam.Referral]) ? 1 : undefined;
            this.metrics.forEach(metric => this.userProfileService.incrementMetric(metric, maxDelay));
        }
    }
}

/**
 * Factory that is responsible for creating UserProfile instrumentation instance
 */
@Injectable({
    providedIn: 'root'
})
export class UserProfileEventFactoryService implements DispatcherEventFactoryService {

    constructor(
        private readonly userProfileService: UserProfileService
    ) {
    }

    public createEvent(
        streamName: string,
        params: InstrumentationParams,
        metrics: Array<string>): InstrumentationEvent {
        return new UserProfileInstrumentationEvent(this.userProfileService, streamName, params, metrics);
    }

    public flush() {
        this.userProfileService.flush();
    }
}
